import { type ClassValue, clsx } from 'clsx';
import { getCountryForTimezone } from 'countries-and-timezones';
import { twMerge } from 'tailwind-merge';
import { differenceInMonths, addMonths, differenceInDays } from 'date-fns';
import { FormattedAddress } from '@/interfaces/clientProfile';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const canvasToFile = (
	canvas: HTMLCanvasElement,
	filename = 'recorte.png'
): Promise<File> =>
	new Promise<File>((resolve) => {
		canvas.toBlob((blob) => {
			if (!blob) throw new Error('Error generando el blob');
			const file = new File([blob], filename, { type: 'image/png' });
			resolve(file);
		}, 'image/png');
	});

let cache: { ip: string; ts: number } | null = null;
const TTL = 30 * 60 * 1000;

function getRuntimeBaseUrl(): string {
	if (typeof window !== 'undefined' && window.location?.origin) {
		return window.location.origin.replace(/\/$/, '');
	}

	if (process.env.NEXT_PUBLIC_SITE_URL) {
		return process.env.NEXT_PUBLIC_SITE_URL.replace(/\/$/, '');
	}

	return process.env.NEXT_PUBLIC_SITE_URL ?? 'http://localhost:3000';
}

export async function getUserIp(): Promise<string> {
	const now = Date.now();
	if (cache && now - cache.ts < TTL) return cache.ip;

	if (typeof window !== 'undefined') {
		const saved = localStorage.getItem('user-ip-cache');
		if (saved) {
			try {
				const obj = JSON.parse(saved) as typeof cache | null;
				if (obj && now - obj.ts < TTL) {
					cache = obj;
					return obj.ip;
				}
			} catch {
				/* corrupted, ignore */
			}
		}
	}

	try {
		const res = await fetch(`${getRuntimeBaseUrl()}/api/ip`, {
			signal: AbortSignal.timeout(5000),
		});
		if (res.ok) {
			const { ip } = (await res.json()) as { ip: string };
			cache = { ip, ts: now };
			if (typeof window !== 'undefined') {
				localStorage.setItem('user-ip-cache', JSON.stringify(cache));
			}
			return ip;
		}
	} catch {
		/* continúa */
	}

	return '';
}

let regionCache: { data: string; ts: number } | null = null;
const REGION_TTL = 60 * 60 * 1000;

function loadRegionFromStorage(): { data: string; ts: number } | null {
	if (typeof window === 'undefined') return null;
	const stored = localStorage.getItem('user-region-cache');
	if (!stored) return null;
	try {
		return JSON.parse(stored);
	} catch {
		return null;
	}
}

function saveRegionToStorage(entry: { data: string; ts: number }) {
	regionCache = entry;
	if (typeof window !== 'undefined') {
		localStorage.setItem('user-region-cache', JSON.stringify(entry));
	}
}

function getCountryFromTimezone(): string | null {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return getCountryForTimezone(tz)?.name ?? null;
	} catch {
		return null;
	}
}

function getCountryFromLocale(): string | null {
	try {
		const langs = navigator.languages?.length
			? navigator.languages
			: [navigator.language];
		const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
		for (const lang of langs) {
			const region = lang.split('-')[1];
			if (region?.length === 2) return displayNames.of(region) ?? null;
		}
	} catch {
		/* ignore */
	}
	return null;
}

export async function getUserRegion(): Promise<string> {
	const now = Date.now();

	// 1. Memory cache
	if (regionCache && now - regionCache.ts < REGION_TTL) {
		return regionCache.data;
	}

	// 2. Storage cache
	const stored = loadRegionFromStorage();
	if (stored && now - stored.ts < REGION_TTL) {
		regionCache = stored;
		return stored.data;
	}

	// 3. Timezone (offline, always available)
	const fromTz = getCountryFromTimezone();
	if (fromTz) {
		saveRegionToStorage({ data: fromTz, ts: now });
		return fromTz;
	}
	console.warn(Intl.DateTimeFormat().resolvedOptions().timeZone);

	// 4. Browser locale
	const fromLocale = getCountryFromLocale();
	if (fromLocale) {
		saveRegionToStorage({ data: fromLocale, ts: now });
		return fromLocale;
	}

	// 5. ipapi.co — último recurso, stale cache si falla
	try {
		const response = await fetch('https://ipapi.co/json/', {
			signal: AbortSignal.timeout(5000),
		});
		if (response.ok) {
			const data = await response.json();
			const country = data.country_name ?? 'Unknown';
			saveRegionToStorage({ data: country, ts: now });
			return country;
		}
		console.warn(response.status);
	} catch (err) {
		console.error('[getUserRegion] ipapi.co fetch failed:', err);
	}

	if (stored?.data) {
		regionCache = stored;
		return stored.data;
	}

	return 'Unknown';
}

export const normalizeError = (error: unknown): Error => {
	if (error instanceof Error) return error;
	try {
		return new Error(typeof error === 'string' ? error : JSON.stringify(error));
	} catch (error) {
		return new Error('Unknown error (non-serializable)');
	}
};

export const getDeviceInfo = () => {
	function getDeviceType() {
		const ua = navigator.userAgent.toLowerCase();

		if (/mobile|iphone|ipod|android.*mobile/.test(ua)) return 'phone';
		if (/ipad|tablet|android(?!.*mobile)/.test(ua)) return 'tablet';
		if (/smart-tv|tv|netcast|appletv/.test(ua)) return 'smart-tv';
		if (/playstation|xbox|nintendo/.test(ua)) return 'console';
		if (/bot|crawl|spider/.test(ua)) return 'bot';

		return 'desktop';
	}

	function getOS() {
		const ua = navigator.userAgent.toLowerCase();
		if (/windows nt/.test(ua)) return 'Windows';
		if (/mac os x/.test(ua)) return 'MacOS';
		if (/android/.test(ua)) return 'Android';
		if (/iphone|ipad|ipod/.test(ua)) return 'iOS';
		if (/linux/.test(ua)) return 'Linux';
		return 'Desconocido';
	}

	function getBrowser() {
		const ua = navigator.userAgent.toLowerCase();

		if (ua.includes('edg')) return 'Edge';
		if (ua.includes('opr') || ua.includes('opera')) return 'Opera';
		if (
			ua.includes('chrome') &&
			!ua.includes('chromium') &&
			!ua.includes('edg')
		)
			return 'Chrome';
		if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
		if (ua.includes('firefox')) return 'Firefox';
		if (ua.includes('brave')) return 'Brave';
		if (ua.includes('vivaldi')) return 'Vivaldi';
		if (ua.includes('chromium')) return 'Chromium';

		return 'Desconocido';
	}

	return {
		deviceType: getDeviceType(),
		os: getOS(),
		browser: getBrowser(),
	};
};

/**
 * Formatea un número para incluir comas como separadores de miles.
 * Ejemplo: 1234567 -> "1,234,567"
 *
 * @param value - El número o string numérico a formatear.
 * @returns El número formateado con separadores de miles.
 */
export function formatWithCommas(value: number | string): string {
	if (value === null || value === undefined) return '0';

	// Convertir a número y verificar que es válido
	const num = Number(value);
	if (isNaN(num)) return String(value);

	// Usar Intl.NumberFormat para mejor compatibilidad
	return new Intl.NumberFormat('en-US').format(num);
}

export function getUUID(): string {
	return crypto.randomUUID();
}

export function monthsSince(dateString: string) {
	const start = new Date(dateString);
	const now = new Date();

	const fullMonths = differenceInMonths(now, start);
	const anchorDate = addMonths(start, fullMonths);

	const daysInCurrentMonth = differenceInDays(
		addMonths(anchorDate, 1),
		anchorDate
	);

	const extraDays = differenceInDays(now, anchorDate);

	const totalMonths = fullMonths + extraDays / daysInCurrentMonth;

	const roundedMonths = Math.max(0, Math.round(totalMonths));

	const label = roundedMonths === 1 ? 'month' : 'months';

	return `${roundedMonths} ${label}`;
}

export const isRichTextEmpty = (value: unknown): boolean => {
	if (typeof value !== 'string') return false;

	// Elimina tags HTML
	const text = value
		.replace(/<[^>]*>/g, '') // remove HTML tags
		.replace(/&nbsp;/g, ' ') // normalize nbsp
		.trim();

	return text.length === 0;
};

const addressAlreadyIncludes = (address: string, part?: string) => {
	if (!part?.trim()) return false;
	const escaped = part.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	return new RegExp(`\\b${escaped}\\b`, 'i').test(address);
};

/** Places used to persist the full Google string in address1 (`street, city, state, country, zip`). Skip re-appending city/state/zip when they are already in that line. */
const withOptionalAddress2 = (line: string, address2?: string) => {
	const line2 = address2?.trim() ?? '';
	if (!line2 || addressAlreadyIncludes(line, line2)) return line;
	if (!line) return line2;

	const comma = line.indexOf(',');
	if (comma === -1) return `${line}, ${line2}`;

	return `${line.slice(0, comma).trim()}, ${line2}${line.slice(comma)}`;
};

export const getFormattedAddress = ({
	address,
	address2,
	state,
	city,
	zipCode,
}: FormattedAddress) => {
	const line = address?.trim() ?? '';
	const alreadyComplete =
		addressAlreadyIncludes(line, zipCode) ||
		(addressAlreadyIncludes(line, city) && addressAlreadyIncludes(line, state));

	if (alreadyComplete) return withOptionalAddress2(line, address2);

	const street = withOptionalAddress2(line, address2);

	return `${street && `${street},`} ${city && `${city},`} ${state ?? ''} ${
		zipCode ?? ''
	}`
		.replace(/\s+/g, ' ')
		.trim();
};
